import {
  UseMutationResult,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { AxiosError } from 'axios';
import { useGTMEvents } from '../events';
import * as queryKeys from '../constants/keys';
import type { IError, PhilBotApp, PhillConnections, UserEntity } from '../utils/types';
import * as api from '../api';

export const useGetUserData = (): UseMutationResult<UserEntity, IError, { token: string }, unknown> => {
  const { events, trackEvent } = useGTMEvents();

  return useMutation({
    mutationFn: ({ token }: { token: string }) => api.login(token),
    onSuccess: data => {
      trackEvent(events.Login, { userId: data.id });
      // for GTM tracking
      Cookies.set('user_id', data.id);
    },
  });
};
export const useLoginWithPassword = (): UseMutationResult<
  UserEntity,
  IError,
  { email: string; password: string },
  unknown
> => {
  const { events, trackEvent } = useGTMEvents();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) => api.loginWithPassword(email, password),
    onSuccess: data => {
      trackEvent(events.Login, { userId: data.id });
      // for GTM tracking
      Cookies.set('user_id', data.id);
    },
  });
};

export const useForgotPassword = (): UseMutationResult<void, IError, { email: string }, unknown> =>
  useMutation({
    mutationFn: ({ email }: { email: string }) => api.forgotPassword(email),
  });

export const useResetPassword = (): UseMutationResult<void, IError, { token: string; password: string }, unknown> =>
  useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) => api.resetPassword(token, password),
  });

export const useSetPassword = (): UseMutationResult<UserEntity, IError, { password: string }, unknown> =>
  useMutation({
    mutationFn: ({ password }: { password: string }) => api.setPassword(password),
  });

export const useChangePassword = (): UseMutationResult<
  UserEntity,
  IError,
  { currentPassword: string; newPassword: string },
  unknown
> =>
  useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      api.changePassword(currentPassword, newPassword),
  });

type RefreshTokenParams = {
  token: string;
};

type UseRefreshTokenOptions = UseMutationOptions<void, AxiosError<IError>, RefreshTokenParams, unknown>;

export const useRefreshToken = (options?: UseRefreshTokenOptions) =>
  useMutation({
    mutationFn: ({ token }: RefreshTokenParams) => api.refresh(token),
    ...options,
  });

/**
 * What Phill may read, answered by the API rather than by this browser.
 *
 * The settings screen used to keep this in `localStorage`, which meant it told a different story on
 * every machine and could not survive a reset. The tokens the API holds are the only truth there is.
 */
export const usePhillConnections = (): UseQueryResult<PhillConnections, IError> =>
  useQuery({
    queryKey: [queryKeys.PHILL_CONNECTIONS],
    queryFn: () => api.fetchConnections(),
    staleTime: 1000,
  });

export const useDisconnectSource = (): UseMutationResult<void, IError, PhilBotApp, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (source: PhilBotApp) => api.disconnectSource(source),
    // Refetched rather than patched in place: the row's state is the API's answer, and a withdrawal
    // that failed halfway should show what is actually held, not what was asked for.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.PHILL_CONNECTIONS] }),
  });
};
